import React, { lazy } from 'react'
import PropTypes from 'prop-types'
import { BillboardText, colors, ListItem, MenuOverlay } from '@fs/zion-ui'
import Cache from '@fs/zion-cache'

import { trackEvent } from '@fs/zion-analytics'
import { useParams } from '@fs/zion-router'
import { useFeatureFlag } from '@fs/zion-flags'

import { useTranslation } from 'react-i18next'
import { CACHE, KEEP_PROCESS_TYPE } from '../../helpers'
import { useGenerateImage } from '../useGenerateImage/useGenerateImage'
import useImageSettings from '../useImageSettings/useImageSettings'

const CancelConfirmationDialog = lazy(
  () => import('../../components/CancelConfirmationDialog/CancelConfirmationDialog')
)

const { DOWNLOAD } = KEEP_PROCESS_TYPE
const visibilityCache = new Cache(CACHE.STORE_VISIBILITY)

export default function DownloadQuickGlance({
  isMultiPage,
  downloadOverlay,
  downloadRef,
  handleProcess,
  showAvatars = false,
  showPortraits = false,
}) {
  const [t] = useTranslation()
  const newPuppeteerPrintFlag = useFeatureFlag('chinese_treeDesigns_newPrint').isOn
  const { siteName, style, design, personId } = useParams()
  const { IMAGE_WIDTH, IMAGE_HEIGHT, backgroundOpacity } = useImageSettings()

  const { generateImage, cancelOverlay, shouldCancel } = useGenerateImage()
  return (
    <>
      <MenuOverlay {...downloadOverlay}>
        <BillboardText inline="false" primaryColor={colors.blue60}>
          <ListItem
            data-testid="jpegDownload"
            onClick={async () => {
              trackEvent({ link_name: `Button:Download Jpeg` })
              if (newPuppeteerPrintFlag) {
                // Get hiddenIds from cache
                const hiddenIds =
                  (await visibilityCache.getItem(CACHE.HIDDEN_IDS(personId))) || CACHE.HIDDEN_IDS_INITIAL

                generateImage({
                  siteName,
                  style,
                  design,
                  personId,
                  width: IMAGE_WIDTH,
                  height: IMAGE_HEIGHT,
                  backgroundOpacity,
                  hiddenIds,
                  showAvatars,
                  showPortraits,
                })
              } else {
                handleProcess({ downloadType: 'jpeg', process: DOWNLOAD, focusBackRef: downloadRef })
              }
            }}
            disabled={isMultiPage}
            primaryText={t('DownloadQuickGlance.button.downloadImage', 'JPEG')}
          />
          <ListItem
            data-testid="pdfDownload"
            onClick={() => {
              trackEvent({ link_name: `Button:Download Pdf` })
              handleProcess({ downloadType: 'pdf', process: DOWNLOAD, focusBackRef: downloadRef })
            }}
            primaryText={t('DownloadQuickGlance.button.downloadPdf', 'PDF')}
          />
        </BillboardText>
      </MenuOverlay>

      {newPuppeteerPrintFlag && (cancelOverlay.isOpen || cancelOverlay.transitioning) && (
        <CancelConfirmationDialog
          cancelOverlay={cancelOverlay}
          process={t('useGenerateImage.cancelProcess', 'Image Generation')}
          shouldCancel={shouldCancel}
        />
      )}
    </>
  )
}

DownloadQuickGlance.propTypes = {
  isMultiPage: PropTypes.bool.isRequired,
  downloadOverlay: PropTypes.shape({
    isOpen: PropTypes.bool,
    transitioning: PropTypes.bool,
    close: PropTypes.func,
    open: PropTypes.func,
    handleClick: PropTypes.func,
  }).isRequired,
  downloadRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.shape({}) })]).isRequired,
  handleProcess: PropTypes.func.isRequired,
  showAvatars: PropTypes.bool,
  showPortraits: PropTypes.bool,
}
